import React, { memo } from 'react';
import { Button, StatusBadge } from '../common';
import PlayerAvatar from '../common/PlayerAvatar';
import { Text, Typography } from '../common/Typography';
import { formatTournamentEntryLabel, getEntryPlayersSorted } from '../../utils/tournamentFormat';
import { getGenderTextColor } from '../../utils/genderColors';
import { formatPlayerName } from '../../utils/nameUtils';

/** Match is effectively complete when games satisfy the format (e.g. Bo3 needs 2 wins). */
const isMatchEffectivelyComplete = (match) => {
  const bestOf = match.best_of ?? 1;
  const gamesNeeded = Math.ceil(bestOf / 2);
  const games = match.games || [];
  const team1Wins = games.filter((g) => g.winning_team === 1).length;
  const team2Wins = games.filter((g) => g.winning_team === 2).length;
  return (team1Wins >= gamesNeeded || team2Wins >= gamesNeeded) && games.length > 0;
};

const isMatchResolved = (match) =>
  ['walkover', 'forfeit', 'completed'].includes(match.status) || isMatchEffectivelyComplete(match);

const renderEntryName = (entry, isSingles, seed) => {
  if (!entry) {
    return <span className="text-text-muted">TBD</span>;
  }

  const players = getEntryPlayersSorted(entry);

  if (isSingles && players[0]) {
    return (
      <>
        <span className={getGenderTextColor(players[0].gender)}>{formatPlayerName(players[0])}</span>
        {seed ? <span className="ml-1 font-normal text-text-muted">({seed})</span> : null}
      </>
    );
  }

  if (players.length >= 2) {
    return (
      <>
        <span className={getGenderTextColor(players[0]?.gender)}>
          {formatPlayerName(players[0])}
        </span>
        <span className="text-text-muted"> & </span>
        <span className={getGenderTextColor(players[1]?.gender)}>
          {formatPlayerName(players[1])}
        </span>
        {seed ? <span className="ml-1 font-normal text-text-muted">({seed})</span> : null}
      </>
    );
  }

  return (
    <>
      {formatTournamentEntryLabel(entry, isSingles)}
      {seed ? <span className="ml-1 font-normal text-text-muted">({seed})</span> : null}
    </>
  );
};

const EntryAvatars = memo(({ entry, isSingles }) => {
  const players = getEntryPlayersSorted(entry);
  if (players.length === 0) return null;

  if (isSingles || players.length === 1) {
    return <PlayerAvatar player={players[0]} size="md" />;
  }

  return (
    <div className="flex shrink-0 -space-x-2">
      <PlayerAvatar player={players[0]} size="md" className="ring-2 ring-surface" />
      <PlayerAvatar player={players[1]} size="md" className="ring-2 ring-surface" />
    </div>
  );
});

EntryAvatars.displayName = 'EntryAvatars';

const ParticipantRow = memo(({ entry, seed, isSingles, teamSide, won, withdrawn, score, showCrown }) => {
  const accentClass = won
    ? 'border-success'
    : teamSide === 1
      ? 'border-team1'
      : 'border-team2';

  return (
  <div
    className={`flex items-center justify-between gap-3 border-l-[3px] py-2.5 pl-3 pr-3 sm:py-3 sm:pl-4 sm:pr-4 ${accentClass} ${
      won ? 'bg-success/10' : 'bg-surface'
    }`}
  >
    <div className="flex min-w-0 flex-1 items-center gap-2.5 sm:gap-3">
      <EntryAvatars entry={entry} isSingles={isSingles} />
      <div className="flex min-w-0 flex-1 items-center gap-2">
      {showCrown ? (
        <span className="shrink-0 text-base" title="Champions" aria-hidden>
          👑
        </span>
      ) : null}
      <Typography variant="strong" className="truncate text-sm sm:text-base">
        {renderEntryName(entry, isSingles, seed)}
      </Typography>
      {withdrawn ? (
        <StatusBadge variant="inactive" size="small" className="shrink-0">
          Withdrawn
        </StatusBadge>
      ) : null}
      {won ? (
        <StatusBadge variant="success" size="small" className="shrink-0">
          Won
        </StatusBadge>
      ) : null}
      </div>
    </div>
    {score != null ? (
      <span className="shrink-0 text-lg font-bold tabular-nums text-text-primary sm:text-xl">
        {score}
      </span>
    ) : null}
  </div>
  );
});

ParticipantRow.displayName = 'ParticipantRow';

/**
 * Tournament match row card — mobile-first face-off layout.
 * @param {'group'|'court'|'knockout'} metaMode
 */
const TournamentMatchCard = memo(({
  match,
  isSingles = false,
  canManage = false,
  onOpenScore,
  metaMode = 'group',
  team1Seed = null,
  team2Seed = null,
  isFinalRound = false,
}) => {
  const resolved = isMatchResolved(match);
  const isWalkover = match.status === 'walkover';
  const isForfeit = match.status === 'forfeit';
  const team1Won = resolved && match.winner_entry_id === match.entry1_id;
  const team2Won = resolved && match.winner_entry_id === match.entry2_id;
  const games = match.games || [];
  const singleGame = games.length === 1;
  const scoreSummary =
    games.length > 0
      ? games.map((g) => `${g.team_1_score}-${g.team_2_score}`).join(', ')
      : isWalkover
        ? 'Walkover'
        : isForfeit
          ? 'Forfeit'
          : null;

  const team1Score = singleGame ? games[0].team_1_score : null;
  const team2Score = singleGame ? games[0].team_2_score : null;

  const handleCardClick = () => {
    if (canManage && !resolved) onOpenScore?.(match);
  };

  const handleActionClick = (event) => {
    event.stopPropagation();
    onOpenScore?.(match);
  };

  return (
    <div
      className={`rounded-xl border p-3 transition-all sm:p-4 ${
        resolved
          ? 'border-border-subtle bg-surface-subtle/70'
          : canManage
            ? 'cursor-pointer border-border-medium bg-surface shadow-brand-sm hover:border-primary/40'
            : 'border-border-medium bg-surface shadow-brand-sm'
      }`}
      onClick={handleCardClick}
      role={canManage && !resolved ? 'button' : undefined}
      tabIndex={canManage && !resolved ? 0 : undefined}
      onKeyDown={(event) => {
        if (!canManage || resolved) return;
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          onOpenScore?.(match);
        }
      }}
    >
      <div className="mb-3 flex items-start justify-between gap-2">
        <div className="flex flex-wrap gap-1.5">
          {metaMode === 'group' && match.court_number ? (
            <StatusBadge variant="default" size="small">
              C{match.court_number}
              {match.court_match_order != null ? ` · M${match.court_match_order}` : ''}
            </StatusBadge>
          ) : null}
          {metaMode === 'court' && match.court_match_order != null ? (
            <StatusBadge variant="default" size="small">
              Match {match.court_match_order}
            </StatusBadge>
          ) : null}
          {metaMode === 'court' && match.group?.name ? (
            <StatusBadge variant="muted" size="small">
              Group {match.group.name}
            </StatusBadge>
          ) : null}
        </div>
        <div className="flex flex-wrap justify-end gap-1">
          {isWalkover ? <StatusBadge variant="warning" size="small">Walkover</StatusBadge> : null}
          {isForfeit ? <StatusBadge variant="warning" size="small">Forfeit</StatusBadge> : null}
          {resolved && !isWalkover && !isForfeit ? (
            <StatusBadge variant="info" size="small">Completed</StatusBadge>
          ) : null}
        </div>
      </div>

      <div className="mb-3 overflow-hidden rounded-lg border border-border-subtle bg-surface">
        <ParticipantRow
          entry={match.team1}
          seed={team1Seed}
          isSingles={isSingles}
          teamSide={1}
          won={team1Won}
          withdrawn={Boolean(match.team1?.withdrawn_at)}
          score={singleGame ? team1Score : null}
          showCrown={isFinalRound && team1Won}
        />
        {!singleGame && scoreSummary ? (
          <div className="border-y border-border-subtle bg-surface-subtle px-3 py-2 text-center">
            <Text className="text-sm font-semibold tabular-nums text-text-primary">{scoreSummary}</Text>
          </div>
        ) : singleGame && !scoreSummary ? (
          <div className="border-y border-border-subtle bg-surface-subtle px-3 py-1.5 text-center">
            <Text className="text-xs font-medium uppercase tracking-wide text-text-muted">vs</Text>
          </div>
        ) : null}
        <ParticipantRow
          entry={match.team2}
          seed={team2Seed}
          isSingles={isSingles}
          teamSide={2}
          won={team2Won}
          withdrawn={Boolean(match.team2?.withdrawn_at)}
          score={singleGame ? team2Score : null}
          showCrown={isFinalRound && team2Won}
        />
      </div>

      {canManage ? (
        <Button
          variant={resolved ? 'ghost' : 'outline'}
          size="small"
          className={
            resolved
              ? 'min-h-[44px] w-full text-text-muted hover:text-text-primary sm:min-h-0 sm:w-auto'
              : 'min-h-[44px] w-full border-border-medium bg-surface font-semibold text-text-primary shadow-brand-sm hover:border-text-primary/30 hover:bg-surface-subtle sm:min-h-0 sm:w-auto'
          }
          onClick={handleActionClick}
        >
          {resolved ? 'Edit score' : 'Enter score'}
        </Button>
      ) : null}
    </div>
  );
});

TournamentMatchCard.displayName = 'TournamentMatchCard';

export default TournamentMatchCard;
